/**
 * Uitgebreide informatiepagina's voor concrete zoekvragen ("wat kost een
 * verhuizing", "piano verhuizen"). De teksten beschrijven alleen hoe wij
 * werkelijk werken; er staan geen verzonnen tarieven in — prijzen krijgt de
 * klant altijd in een offerte op maat.
 */
export type Guide = {
  slug: string;
  eyebrow: string;
  h1: string;
  seoTitle: string;
  seoDescription: string;
  intro: string;
  keywords: string[];
  image: string;
  sections: { h2: string; body: string; bullets?: string[] }[];
  faq: { q: string; a: string }[];
};

export const guides: Guide[] = [
  {
    slug: "verhuiskosten",
    eyebrow: "Verhuiskosten",
    h1: "Wat kost een verhuizing? Zo bouwen wij onze prijs op",
    seoTitle: "Wat kost een verhuizing? Verhuiskosten uitgelegd | Vakkundig",
    seoDescription:
      "Verhuiskosten begrijpelijk uitgelegd: welke factoren de prijs bepalen (kubieke meters, verdieping, verhuislift, inpakservice, afstand) en hoe je een offerte op maat krijgt van Vakkundig Verhuizingen & Transport.",
    intro:
      "Een verhuizing kost bij ieder huishouden iets anders, omdat geen enkele woning hetzelfde is. In plaats van een standaardprijs die later toch niet klopt, rekenen wij per situatie. Hieronder leggen we precies uit welke factoren meewegen, zodat je zelf kunt inschatten waar jouw verhuizing in valt en waarom onze offerte eruitziet zoals hij eruitziet.",
    keywords: [
      "wat kost een verhuizing",
      "verhuiskosten",
      "verhuisbedrijf prijzen",
      "prijs verhuisbedrijf",
      "verhuizing berekenen",
      "verhuisofferte aanvragen",
    ],
    image: "dozen",
    sections: [
      {
        h2: "De vijf factoren die de prijs bepalen",
        body: "Wij kijken bij elke aanvraag naar dezelfde punten. Hoe beter je die doorgeeft, hoe scherper en betrouwbaarder de prijs die je van ons krijgt.",
        bullets: [
          "Hoeveelheid inboedel in kubieke meters — met de inboedelcalculator op onze offertepagina reken je dit zelf uit.",
          "Verdieping en bereikbaarheid: begane grond, eerste etage of hoger, met of zonder lift in het gebouw.",
          "Of er een verhuislift nodig is, bijvoorbeeld bij een smal trappenhuis of grote meubels. Onze verhuislift met bediener huur je vanaf € 95 per uur.",
          "Extra werk: demontage en montage van kasten en bedden, inpakservice, opslag of het meenemen van afval.",
          "Afstand tussen het oude en nieuwe adres en het aantal ritten dat daarvoor nodig is.",
        ],
      },
      {
        h2: "Waarom een offerte op maat goedkoper uitpakt",
        body: "Een vast uurtarief zonder plan betekent vaak dat je betaalt voor zoekwerk en improviseren. Wij bepalen vooraf hoeveel verhuizers, welke bus en welk materiaal nodig zijn. Bij grotere verhuizingen komen we langs of doen we een videobezichtiging, zodat de dag zelf vlot verloopt. Je krijgt één prijs waar alles in staat, dus geen onverwachte posten achteraf.",
      },
      {
        h2: "Wat er standaard bij zit",
        body: "In onze prijs zitten ervaren verhuizers, een verhuiswagen met dekens en wikkelfolie, het zorgvuldig in- en uitladen en het plaatsen van je meubels in de juiste kamer. Verzekerd vervoer hoort daar ook bij: wij werken met een NIWO-vergunning voor beroepsgoederenvervoer.",
      },
      {
        h2: "Zo kom je snel aan een prijs",
        body: "Vul het offerteformulier in met je adressen, verdieping en inboedel, of stuur ons een WhatsApp-bericht met een paar foto's van je woning. Meestal krijg je dezelfde werkdag antwoord. Twijfel je over de hoeveelheid? Bel ons, dan schatten we samen de kubieke meters in.",
      },
    ],
    faq: [
      {
        q: "Kan ik online een prijs berekenen voor mijn verhuizing?",
        a: "Je kunt met onze inboedelcalculator het aantal kubieke meters berekenen. De definitieve prijs krijg je in een offerte op maat, omdat verdieping, bereikbaarheid en extra werk sterk meewegen.",
      },
      {
        q: "Rekenen jullie per uur of een vaste prijs?",
        a: "Wij werken met een duidelijke prijs op maat vooraf, zodat je niet voor verrassingen komt te staan. Bij kleine opdrachten, zoals een enkel meubelstuk of transport, kan een korter tarief passender zijn — dat bespreken we eerlijk met je.",
      },
      {
        q: "Wat maakt een verhuizing duurder dan verwacht?",
        a: "Meestal is dat een hogere verdieping zonder lift, meer inboedel dan gedacht of extra werk zoals demonteren en monteren. Geef die punten daarom bij je aanvraag door, dan zit de prijs vanaf het begin goed.",
      },
      {
        q: "Zit het inpakken bij de prijs in?",
        a: "Inpakservice is een aparte optie. Je kunt kiezen voor volledig inpakken, alleen breekbare zaken en de keuken, of alleen demontage en montage van kasten en bedden.",
      },
    ],
  },
  {
    slug: "piano-verhuizen",
    eyebrow: "Speciaal transport",
    h1: "Piano verhuizen: veilig transport door ervaren verhuizers",
    seoTitle: "Piano verhuizen | Pianotransport door heel Nederland | Vakkundig",
    seoDescription:
      "Piano of vleugel verhuizen door ervaren verhuizers: veilig inpakken, verhuislift bij een smalle trap en verzekerd transport door heel Nederland. Vraag een prijs op maat aan bij Vakkundig Verhuizingen & Transport.",
    intro:
      "Een piano is zwaar, kwetsbaar en zit vaak op een plek waar je hem nauwelijks weg krijgt. Wij verhuizen ze regelmatig, van een klavecimbel op driehoog tot een vleugel die via de verhuislift naar buiten gaat. Met de juiste mensen, banden en dekens is dat gewoon vakwerk.",
    keywords: [
      "piano verhuizen",
      "pianotransport",
      "vleugel verhuizen",
      "piano verhuizen den haag",
      "piano transport nederland",
    ],
    image: "team",
    sections: [
      {
        h2: "Hoe wij een piano verhuizen",
        body: "We bekijken eerst de route: trapbreedte, bochten, deurbreedte en drempels. Daarna beslissen we of de piano via de trap kan of dat de verhuislift veiliger is. De piano wordt volledig ingepakt in verhuisdekens en met banden vastgezet, zodat er niets kan schuiven tijdens het rijden.",
        bullets: [
          "Altijd met voldoende verhuizers, nooit met te weinig mankracht",
          "Verhuisdekens, wikkelfolie en spanbanden voor de kwetsbare kast en poten",
          "Verhuislift tot acht hoog wanneer de trap te smal of te steil is",
          "Verzekerd vervoer met NIWO-vergunning",
        ],
      },
      {
        h2: "Piano met of zonder verhuislift",
        body: "In veel oudere woningen is het trappenhuis simpelweg te smal. Met onze verhuislift gaat de piano via het raam of balkon naar buiten — vaak sneller en veiliger dan sjouwen, en meestal hoeft er minder gedemonteerd te worden.",
      },
      {
        h2: "Alleen de piano, of de hele verhuizing",
        body: "We doen zowel losse pianotransporten als complete verhuizingen waarin de piano meegaat. Combineer je het, dan hoeft er maar één keer een team en een wagen te komen en is dat voordeliger.",
      },
    ],
    faq: [
      {
        q: "Verhuizen jullie ook een vleugel?",
        a: "Ja. Een vleugel wordt bij ons ingepakt en waar nodig gedeeltelijk gedemonteerd, en met voldoende verhuizers en materiaal vervoerd.",
      },
      {
        q: "Wat kost een piano verhuizen?",
        a: "Dat hangt af van de verdieping, of er een verhuislift nodig is en de afstand. Stuur ons een paar foto's van de piano en het trappenhuis via WhatsApp, dan geven we snel een prijs op maat.",
      },
      {
        q: "Moet de piano na de verhuizing gestemd worden?",
        a: "Een piano heeft na transport meestal tijd nodig om te acclimatiseren; stemmen doe je daarna het beste. Wij verzorgen het vervoer, het stemmen laat je door een pianotechnicus doen.",
      },
    ],
  },
  {
    slug: "studentenverhuizing",
    eyebrow: "Studentenverhuizing",
    h1: "Studentenverhuizing: snel en voordelig naar je nieuwe kamer",
    seoTitle: "Studentenverhuizing | Snel en voordelig verhuizen | Vakkundig",
    seoDescription:
      "Studentenverhuizing door heel Nederland: kamer, studio of appartement, met of zonder verhuislift. Voordelig verhuizen met ervaren verhuizers van Vakkundig Verhuizingen & Transport.",
    intro:
      "Een kamer of studio verhuizen is klein werk met grote haast: sleutels op één dag, een smalle trap en meestal een strak budget. Wij doen dit soort verhuizingen vaak en houden ze compact — één bus, twee verhuizers en klaar voordat de dag om is.",
    keywords: [
      "studentenverhuizing",
      "studenten verhuizen",
      "kamer verhuizen",
      "goedkoop verhuizen student",
      "studentenverhuizing den haag",
    ],
    image: "dozen",
    sections: [
      {
        h2: "Waarom een studentenverhuizing anders werkt",
        body: "Er is vaak weinig inboedel, maar wel een lastig trappenhuis, een deelbare buitendeur en een strakke overdrachtstijd. We plannen daarom kort en gericht: ophalen, rijden, plaatsen. Bed en bureau demonteren we op verzoek en zetten we in de nieuwe kamer weer in elkaar.",
        bullets: [
          "Compact team en een bus die past in een smalle stad",
          "Demontage en montage van bed, bureau en kledingkast",
          "Verhuisdekens en wikkelfolie voor matras en beeldscherm",
          "Ook in het weekend, zodat je geen les of werk mist",
        ],
      },
      {
        h2: "Tussen twee kamers, of naar een andere stad",
        body: "Verhuis je binnen dezelfde stad, dan is het vaak in een paar uur klaar. Ga je van bijvoorbeeld Den Haag naar Utrecht of Groningen, dan rijden we in één rit door — we werken in heel Nederland.",
      },
      {
        h2: "Tijdelijk opslag nodig",
        body: "Loopt je nieuwe kamer nog niet leeg of ga je een periode naar het buitenland? Dan zetten we je inboedel bij ons in de opslag en brengen we hem later naar je nieuwe adres.",
      },
    ],
    faq: [
      {
        q: "Wat kost een studentenverhuizing?",
        a: "Dat hangt af van de hoeveelheid spullen, de verdieping en de afstand. Omdat het meestal om weinig kubieke meters gaat, blijft de prijs beperkt. Vraag een offerte op maat aan, dan weet je het precies.",
      },
      {
        q: "Kunnen jullie op korte termijn verhuizen?",
        a: "Vaak wel. Bel of app ons met je datum, dan kijken we direct in de planning; we zijn zeven dagen per week bereikbaar.",
      },
      {
        q: "Moet ik zelf inpakken?",
        a: "Dat mag, maar het hoeft niet. Je kunt onze inpakservice voor de breekbare spullen en de keuken bijboeken.",
      },
    ],
  },
  {
    slug: "internationaal-verhuizen",
    eyebrow: "Internationaal verhuizen",
    h1: "Internationaal verhuizen vanuit Nederland",
    seoTitle: "Internationaal verhuizen | Verhuizen naar het buitenland | Vakkundig",
    seoDescription:
      "Internationaal verhuizen vanuit Nederland: complete verhuizing naar het buitenland met inpakservice, verhuislift, opslag en verzekerd transport door Vakkundig Verhuizingen & Transport.",
    intro:
      "Verhuizen naar het buitenland vraagt meer voorbereiding dan een verhuizing om de hoek. Wij verzorgen internationale verhuizingen vanuit Nederland: van het inpakken en de verhuislift bij vertrek tot het lossen op je nieuwe adres.",
    keywords: [
      "internationaal verhuizen",
      "verhuizen naar het buitenland",
      "internationale verhuizing nederland",
      "verhuisbedrijf buitenland",
      "emigreren verhuizen",
    ],
    image: "team",
    sections: [
      {
        h2: "Wat wij voor een internationale verhuizing regelen",
        body: "We beginnen met een goede inventarisatie van je inboedel in kubieke meters; dat bepaalt de wagen en de planning. Daarna pakken we in met verhuisdekens en wikkelfolie, laden we zorgvuldig en rijden we in één keer door naar de bestemming.",
        bullets: [
          "Inpakservice en demontage van kasten en bedden",
          "Verhuislift tot acht hoog bij vertrek, ook bij smalle trappenhuizen",
          "Tijdelijke opslag als je nieuwe woning nog niet klaar is",
          "Verzekerd transport met NIWO-vergunning voor beroepsgoederenvervoer",
        ],
      },
      {
        h2: "Planning en overdracht",
        body: "Bij een internationale verhuizing is timing alles: sleuteloverdracht, aankomst en soms een periode ertussen. We spreken met je één planning af en houden je onderweg op de hoogte, zodat je weet wanneer we er zijn.",
      },
      {
        h2: "Deels meenemen, deels opslaan",
        body: "Veel mensen nemen niet alles mee. We kunnen je inboedel splitsen: het deel dat meegaat, en het deel dat hier in de opslag blijft tot je terugkomt of het laat nasturen.",
      },
    ],
    faq: [
      {
        q: "Naar welke landen verhuizen jullie?",
        a: "Wij rijden internationaal vanuit Nederland. Geef je bestemming door in je aanvraag, dan laten we weten hoe we het inplannen en wat het kost.",
      },
      {
        q: "Kunnen jullie mijn inboedel tijdelijk opslaan?",
        a: "Ja, we hebben opslag beschikbaar. Handig als je later vertrekt of je nieuwe woning nog niet beschikbaar is.",
      },
      {
        q: "Hoe lang vooraf moet ik boeken?",
        a: "Voor een internationale verhuizing plannen we het liefst een paar weken vooruit, maar bel ons ook bij een korte termijn — vaak vinden we een oplossing.",
      },
    ],
  },
  {
    slug: "kantoorverhuizing",
    eyebrow: "Kantoorverhuizing",
    h1: "Kantoorverhuizing: verhuizen zonder dat het werk stilstaat",
    seoTitle: "Kantoorverhuizing | Bedrijfsverhuizing door heel Nederland | Vakkundig",
    seoDescription:
      "Kantoorverhuizing of bedrijfsverhuizing door ervaren verhuizers: bureaus en kasten demonteren en monteren, archief en ICT zorgvuldig vervoeren, verhuizen in het weekend of 's avonds. Vraag een prijs op maat aan bij Vakkundig Verhuizingen & Transport.",
    intro:
      "Een kantoor verhuizen is vooral een planningsvraagstuk: iedereen moet maandag weer aan het werk kunnen. Wij verhuizen kantoren, praktijken, werkplaatsen en winkels, meestal buiten kantoortijden of in het weekend, zodat er geen werkdag verloren gaat.",
    keywords: [
      "kantoorverhuizing",
      "bedrijfsverhuizing",
      "kantoor verhuizen",
      "zakelijke verhuizing",
      "kantoorverhuizing den haag",
      "bedrijfsverhuizing offerte",
    ],
    image: "zakelijk",
    sections: [
      {
        h2: "Wat wij bij een kantoorverhuizing doen",
        body: "We lopen vooraf met je door het pand: hoeveel werkplekken, welke kasten en archieven, welke apparatuur en hoe de bereikbaarheid is bij het laden en lossen. Daarna maken we één planning met vaste tijden, zodat je medewerkers precies weten wat er van hen wordt verwacht.",
        bullets: [
          "Bureaus, kasten en vergadertafels demonteren en op de nieuwe plek weer monteren",
          "Archief en dossiers in verhuisdozen of rolcontainers, per afdeling gelabeld",
          "Computers, monitoren en printers ingepakt in folie en verhuisdekens",
          "Verhuislift tot acht hoog als de trap of lift te klein is",
          "Verhuizen in het weekend of 's avonds, zodat het werk doorgaat",
        ],
      },
      {
        h2: "Eén aanspreekpunt van begin tot eind",
        body: "Je krijgt één contactpersoon die de verhuizing plant en op de dag zelf aanwezig is. Zo hoef je niet met losse partijen te schakelen en weet je altijd waar je terecht kunt.",
      },
      {
        h2: "Ook opslag, ontruiming en inrichting",
        body: "Gaat je nieuwe pand nog verbouwd worden? Dan zetten we de inventaris tijdelijk bij ons in de opslag. Meubilair dat niet meegaat voeren we verantwoord af, zodat je het oude pand netjes en leeg oplevert.",
      },
      {
        h2: "Referenties",
        body: "Voor de Gemeente Den Haag verzorgen wij de complete logistieke coördinatie, opbouw en afbouw van alle stemlocaties tijdens de verkiezingen, en bij World Forum The Hague zijn we een vertrouwd gezicht voor de logistieke coördinatie. Daardoor zijn we gewend aan strakke planningen, beveiligde panden en vlekkeloze uitvoering.",
      },
    ],
    faq: [
      {
        q: "Wat kost een kantoorverhuizing?",
        a: "Dat hangt af van het aantal werkplekken, de hoeveelheid archief en inventaris, de verdieping en of er buiten kantoortijden gewerkt wordt. Wij komen langs voor een opname en geven daarna een prijs op maat, zodat je geen verrassingen krijgt.",
      },
      {
        q: "Hoe voorkomen jullie dat ons werk stilvalt?",
        a: "Met een gefaseerd verhuisplan en een planning die we vooraf met jullie afstemmen. Zo kan iedereen direct na de verhuizing op de nieuwe locatie verder werken.",
      },
      {
        q: "Verhuizen jullie ook de computers en het netwerk?",
        a: "Wij verpakken en vervoeren computers, monitoren en randapparatuur zorgvuldig en zetten ze op de juiste werkplek terug. Het aansluiten van servers en netwerk doet je eigen ICT-partij.",
      },
      {
        q: "Hoe lang vooraf moet ik een bedrijfsverhuizing plannen?",
        a: "Voor een kantoor plannen we het liefst enkele weken vooruit, zodat we de opname en de planning goed kunnen doen. Bij spoed bellen kan altijd — vaak vinden we een oplossing.",
      },
    ],
  },
  {
    slug: "woningontruiming-den-haag",
    eyebrow: "Woningontruiming",
    h1: "Woningontruiming in Den Haag: bezemschoon opgeleverd",
    seoTitle: "Woningontruiming Den Haag | Woning leeghalen | Vakkundig",
    seoDescription:
      "Woningontruiming in Den Haag en omgeving: woning, appartement of bedrijfsruimte volledig leeghalen, verantwoorde afvoer via Renewi en de milieustraat en bezemschoon opgeleverd. Vraag een prijs op maat aan bij Vakkundig Verhuizingen & Transport.",
    intro:
      "Een woning leeghalen komt vaak op een moeilijk moment: na een overlijden, bij een verhuizing naar een zorginstelling of als een huurwoning opgeleverd moet worden. Wij ontruimen woningen in Den Haag en omgeving volledig, van zolder tot berging, en leveren de woning bezemschoon op.",
    keywords: [
      "woningontruiming den haag",
      "woning ontruimen den haag",
      "ontruiming den haag",
      "woning leeghalen",
      "inboedel ontruiming",
      "bezemschoon opleveren",
    ],
    image: "ontruiming",
    sections: [
      {
        h2: "Hoe een ontruiming bij ons verloopt",
        body: "We nemen eerst de woning met je op, ook de zolder, berging, kelder en tuin. Daarna spreken we af wat bewaard, geschonken of afgevoerd wordt. Op de dag zelf halen we de woning leeg, vegen we alles na en leveren we op zoals de woningcorporatie of verhuurder het vraagt.",
        bullets: [
          "Volledige ontruiming van woning, appartement, berging en bedrijfsruimte",
          "Spullen die je wilt bewaren apart gehouden en bij je afgeleverd",
          "Verantwoorde afvoer via Renewi en de milieustraat, gescheiden per soort",
          "Verhuislift bij een smalle trap of hoge verdieping",
          "Bezemschoon opgeleverd, klaar voor de eindinspectie",
        ],
      },
      {
        h2: "Rustig en respectvol werken",
        body: "Bij een ontruiming na een overlijden nemen we de tijd. Je kunt aanwezig zijn of het volledig aan ons overlaten; in dat geval sturen we foto's van de opgeleverde woning. Waardevolle of persoonlijke zaken leggen we altijd apart en overleggen we voordat er iets weggaat.",
      },
      {
        h2: "Ook in de omgeving van Den Haag",
        body: "Naast Den Haag ontruimen we onder andere in Rijswijk, Delft, Zoetermeer, Leidschendam, Voorburg, Wassenaar, Leiden en Rotterdam. Wij werken in heel Nederland, maar in de regio Den Haag zijn we meestal snel beschikbaar.",
      },
    ],
    faq: [
      {
        q: "Wat kost een woningontruiming in Den Haag?",
        a: "De prijs hangt af van de hoeveelheid inboedel in kubieke meters, de verdieping en bereikbaarheid en de hoeveelheid afval die verantwoord afgevoerd moet worden. Na een korte opname weet je precies waar je aan toe bent.",
      },
      {
        q: "Wordt de woning bezemschoon opgeleverd?",
        a: "Ja. Standaard halen we de woning volledig leeg en vegen we alles na, zodat je hem kunt opleveren aan de verhuurder of woningcorporatie.",
      },
      {
        q: "Wat gebeurt er met de spullen?",
        a: "Wat nog goed is, geven we waar mogelijk een tweede leven. De rest voeren we gescheiden af via Renewi en de milieustraat, zodat het verantwoord verwerkt wordt.",
      },
      {
        q: "Hoe snel kunnen jullie ontruimen?",
        a: "Vaak binnen enkele dagen. Bij een opleverdatum die dichtbij is, bel of app ons; we zijn zeven dagen per week bereikbaar en plannen dan met voorrang in.",
      },
    ],
  },
  {
    slug: "spoedverhuizing",
    eyebrow: "Spoedverhuizing",
    h1: "Spoedverhuizing in Zuid-Holland: snel verhuizen op korte termijn",
    seoTitle: "Spoedverhuizing Den Haag & Zuid-Holland | Vakkundig",
    seoDescription:
      "Spoedverhuizing nodig in Den Haag, Rijswijk, Delft of Zoetermeer? Snel verhuizen op korte termijn met verhuislift en inpakservice. Trustoo 9,9. Bel 085 060 1023.",
    intro:
      "Soms kan een verhuizing niet wachten: de sleutels moeten eerder in, een huurcontract loopt af of er is thuis iets veranderd. Als verhuisbedrijf in Zuid-Holland houden wij ruimte in de planning voor spoed in Den Haag, Rijswijk, Delft, Zoetermeer en omgeving, en zijn we zeven dagen per week bereikbaar, ook via WhatsApp.",
    keywords: [
      "spoedverhuizing",
      "snel verhuizen",
      "verhuizen op korte termijn",
      "verhuisbedrijf spoed",
      "spoedverhuizing senioren",
      "verhuizen binnen een week",
    ],
    image: "particulier",
    sections: [
      {
        h2: "Hoe snel kan het",
        body: "Bel of app ons met je adressen, verdieping en een idee van de hoeveelheid spullen; het liefst met een paar foto's van je woning. We kijken direct in de planning en kunnen vaak binnen een paar dagen komen, soms dezelfde week nog in het weekend.",
        bullets: [
          "Zeven dagen per week bereikbaar, ook via WhatsApp",
          "Snelle prijsindicatie op basis van foto's, zonder dat we langs hoeven",
          "Inpakservice als je zelf geen tijd hebt om in te pakken",
          "Verhuislift tot acht hoog, zodat het sjouwen over de trap niet vertraagt",
          "Tijdelijke opslag als je nieuwe woning nog niet klaar is",
        ],
      },
      {
        h2: "Spoedverhuizing voor senioren",
        body: "Bij een plotselinge verhuizing naar een aanleunwoning of zorginstelling moet het vaak snel én zorgvuldig. Wij verhuizen de spullen die meegaan, richten de nieuwe kamer in en ontruimen de oude woning bezemschoon, in één opdracht.",
      },
      {
        h2: "Snel hoeft niet slordig",
        body: "Ook bij spoed werken we met dezelfde bescherming: verhuisdekens, wikkelfolie en spanbanden, en demontage en montage van kasten en bedden. We werken met meer verhuizers als het sneller moet, niet met minder zorg.",
      },
      {
        h2: "Spoedverhuizing in Den Haag en omgeving",
        body: "Wij voeren spoedverhuizingen uit in heel Zuid-Holland, met de regio Den Haag als thuisbasis: Den Haag, Rijswijk, Delft, Zoetermeer, Leidschendam-Voorburg, Wassenaar, Pijnacker-Nootdorp, Westland, Leiden en Rotterdam. Doordat we dicht bij huis werken, kunnen we bij spoed vaak snel schakelen.",
        bullets: [
          "Verhuislift vanaf € 95 per uur inclusief bediener, handig bij flats en smalle trappenhuizen",
          "Beoordeeld met een 9,9 op Trustoo",
          "Direct contact: bel 085 060 1023 of app 06 84566663",
        ],
      },
    ],
    faq: [
      {
        q: "Kunnen jullie morgen al verhuizen?",
        a: "Soms wel. Het hangt af van onze planning en de hoeveelheid spullen. Bel 085 060 1023 of app ons, dan weet je binnen een paar minuten wat er mogelijk is.",
      },
      {
        q: "Wat kost een spoedverhuizing?",
        a: "Wij rekenen geen vast spoedtarief; de prijs hangt net als anders af van de hoeveelheid inboedel, de verdieping en de afstand. Wel is er soms een groter team nodig om het op tijd af te ronden, en dat nemen we in de offerte mee.",
      },
      {
        q: "Wat moet ik zelf doen bij een spoedverhuizing?",
        a: "Zo weinig mogelijk. Als er geen tijd is om in te pakken, doen wij dat met de inpakservice; jij hoeft dan alleen aan te geven wat mee moet en wat niet.",
      },
      {
        q: "Verhuizen jullie ook in het weekend?",
        a: "Ja, wij verhuizen zeven dagen per week, dus ook op zaterdag en zondag. Dat is bij spoed vaak de snelste optie.",
      },
    ],
  },
];

export function getGuide(slug: string) {
  return guides.find((g) => g.slug === slug);
}
